import React from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, InputGroup, Badge,
} from 'react-bootstrap';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BeakerIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import {
  useCompanyIngredients,
  CATEGORY_OPTIONS,
  UNIT_OPTIONS,
} from './hooks/useCompanyIngredients';

const thStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 600,
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '12px 16px',
};

const CompanyIngredients: React.FC = () => {
  const {
    isExpired,
    loading,
    search,
    setSearch,
    categoryFilter,
    setCategoryFilter,
    filtered,
    ingredients,
    showModal,
    editingIngredient,
    form,
    setForm,
    saving,
    feedback,
    setFeedback,
    deleteState,
    handleOpenCreate,
    handleOpenEdit,
    handleSave,
    handleConfirmDelete,
    handleDelete,
    closeModal,
    closeDeleteModal,
  } = useCompanyIngredients();

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading ingredients...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setFeedback(prev => ({ ...prev, success: '' }))}
            className="mb-3"
          >
            {feedback.success}
          </Alert>
        )}
        {feedback.error && !showModal && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setFeedback(prev => ({ ...prev, error: '' }))}
            className="mb-3"
          >
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{
              backgroundColor: '#ecfdf5',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <BeakerIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Ingredients</h4>
            <Badge bg="" style={{
              backgroundColor: '#ecfdf5',
              color: '#059669',
              fontSize: '0.75rem',
              fontWeight: '600',
              padding: '4px 10px',
              borderRadius: '6px',
            }}>
              {ingredients.length}
            </Badge>
          </div>
          <Button
            variant="success"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={handleOpenCreate}
            disabled={isExpired}
            style={{ padding: '6px 14px' }}
          >
            <PlusIcon style={{ width: '15px', height: '15px' }} />
            Add Ingredient
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                    <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name or supplier..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }}
                  />
                  {search && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearch('')} style={{ borderRadius: '6px' }}>
                      <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select
                  size="sm"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '6px' }}
                >
                  <option value="all">All Categories</option>
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={3} className="text-muted" style={{ fontSize: '0.85rem' }}>
                Showing {filtered.length} of {ingredients.length} ingredients
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Category</th>
                  <th style={thStyle}>Unit</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Cost / Unit</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Supplier</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Producer</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <BeakerIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginBottom: '8px' }} />
                      <div>
                        {search || categoryFilter !== 'all'
                          ? 'No ingredients match your filters.'
                          : 'No ingredients yet. Add your first ingredient.'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((ingredient) => (
                    <tr key={ingredient.id}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>
                        {ingredient.name}
                      </td>
                      <td style={{ padding: '12px 16px' }} className="d-none d-md-table-cell">
                        <Badge bg="" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 500 }}>
                          {ingredient.category_display}
                        </Badge>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }}>{ingredient.unit_display}</td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                        {ingredient.cost_per_unit ? `${parseFloat(ingredient.cost_per_unit).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-lg-table-cell">
                        {ingredient.supplier || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-lg-table-cell">
                        {ingredient.producer || '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleOpenEdit(ingredient)}
                            style={{ padding: '4px 8px' }}
                            title="Edit"
                          >
                            <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleConfirmDelete(ingredient)}
                            style={{ padding: '4px 8px' }}
                            title="Delete"
                          >
                            <TrashIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            {editingIngredient ? 'Edit Ingredient' : 'Add New Ingredient'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feedback.error && (
            <Alert variant="danger" className="mb-3">{feedback.error}</Alert>
          )}
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Organic Whole Wheat Flour"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Category</Form.Label>
                <Form.Select size="sm" value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}>
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Costing Unit</Form.Label>
                <Form.Select size="sm" value={form.unit} onChange={(e) => setForm(prev => ({ ...prev, unit: e.target.value }))}>
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Cost per Unit (excl. VAT)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  step="0.0001"
                  value={form.cost_per_unit}
                  onChange={(e) => setForm(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Netto Weight (g)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.netto_weight_g}
                  onChange={(e) => setForm(prev => ({ ...prev, netto_weight_g: e.target.value }))}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Gross Weight (g)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.gross_weight_g}
                  onChange={(e) => setForm(prev => ({ ...prev, gross_weight_g: e.target.value }))}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Supplier</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.supplier}
                  onChange={(e) => setForm(prev => ({ ...prev, supplier: e.target.value }))}
                  placeholder="Supplier name"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Producer</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.producer}
                  onChange={(e) => setForm(prev => ({ ...prev, producer: e.target.value }))}
                  placeholder="Producer / brand"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Nutritional info */}
          <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
          <Row className="g-3 mb-3">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626' }}>
                  Critical Stock Level
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.critical_amount}
                  onChange={(e) => setForm(prev => ({ ...prev, critical_amount: e.target.value }))}
                  placeholder="e.g. 5"
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Dashboard will warn when stock reaches this amount. Unit: {form.unit}.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
          <div className="mb-2" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            Nutritional Information (per 100g)
          </div>
          <Row className="g-3">
            {([
              { key: 'calories', label: 'Calories (kcal)' },
              { key: 'protein_g', label: 'Protein (g)' },
              { key: 'carbs_g', label: 'Carbohydrates (g)' },
              { key: 'fat_g', label: 'Fat (g)' },
              { key: 'fiber_g', label: 'Fiber (g)' },
              { key: 'sugar_g', label: 'Sugar (g)' },
            ] as { key: keyof typeof form; label: string }[]).map(({ key, label }) => (
              <Col xs={6} md={4} key={key}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>{label}</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form[key]}
                    onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder="—"
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
          <Button variant="outline-secondary" size="sm" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : (editingIngredient ? 'Save Changes' : 'Add Ingredient')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={deleteState.showModal} onHide={closeDeleteModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Delete Ingredient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete <strong>{deleteState.ingredient?.name}</strong>? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeDeleteModal} disabled={deleteState.deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteState.deleting}>
            {deleteState.deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanyIngredients;
